<template>
  <div class="page showcase" :lang="zh ? 'zh-Hant' : 'en'">
    <!-- 頁首：近作滿版輪播（2026-10-03 哲宇：「人家進來就可以很漂亮的看到」） -->
    <section class="sc-hero" :aria-label="zh ? '近作' : 'Recent work'">
      <router-link v-for="(s, i) in heroSlides" :key="s.key" class="sc-hero-slide" :class="{ on: i === slide }" :to="s.to" tabindex="-1" aria-hidden="true">
        <img :src="s.image" :alt="s.alt" :width="s.width" :height="s.height" :loading="i === 0 ? 'eager' : 'lazy'" :fetchpriority="i === 0 ? 'high' : 'auto'" decoding="async" />
      </router-link>
      <div class="sc-hero-cap">
        <div>
          <p class="sc-eyebrow">{{ zh ? `Works & Exhibitions · ${yearSpan}` : `Che-Yu Wu · ${yearSpan}` }}</p>
          <h1>{{ zh ? '作品與展覽' : 'Works & Exhibitions' }}</h1>
          <p class="sc-count"><b>{{ counts.works }}</b> {{ zh ? '件作品' : 'works' }}<span>·</span><b>{{ counts.exhibitions }}</b> {{ zh ? '場展覽' : 'exhibitions' }}<span>·</span><router-link :to="local('/work/archive/')">{{ zh ? `完整檔案 ${counts.archive} 筆` : `Full archive, ${counts.archive} records` }} →</router-link></p>
        </div>
        <router-link v-if="heroSlides[slide]" class="sc-hero-now" :to="heroSlides[slide].to">
          <span>{{ heroSlides[slide].meta }}</span><b>{{ heroSlides[slide].title }}</b><i>{{ zh ? '進入作品 →' : 'View work →' }}</i>
        </router-link>
      </div>
      <div class="sc-dots" v-if="heroSlides.length > 1">
        <button v-for="(s, i) in heroSlides" :key="s.key" type="button" :class="{ on: i === slide }" :aria-label="s.title" :aria-pressed="i === slide" @click="go(i)"></button>
      </div>
    </section>

    <div class="sc-wrap">
      <!-- 策展論述：輔助的導言，可展開全文 -->
      <section class="sc-essay" :aria-label="zh ? '策展論述' : 'Curatorial essay'">
        <p class="sc-label">{{ zh ? '策展論述' : 'Curatorial essay' }}</p>
        <p class="sc-lede">{{ essay.lede }}</p>
        <div class="sc-essay-body">
          <p v-for="(p, i) in essay.body" :key="i">{{ p }}</p>
          <details v-if="essay.full?.length" class="sc-essay-full">
            <summary>{{ zh ? '閱讀完整策展論述' : 'Read the full essay' }}</summary>
            <template v-for="(p, i) in essay.full" :key="'f' + i">
              <h3 v-if="p.h">{{ p.h }}</h3>
              <blockquote v-else-if="p.q" class="quote"><p>{{ p.q }}</p><cite>{{ p.cite }}</cite></blockquote>
              <p v-else>{{ p.p || p }}</p>
            </template>
          </details>
        </div>
        <blockquote v-if="essay.sig" class="sc-sig"><p>{{ essay.sig.q }}</p><cite>{{ essay.sig.cite }}</cite></blockquote>
      </section>

      <!-- 篩選列：類型＋五條線；黏在頁首下 -->
      <nav class="sc-bar" :aria-label="zh ? '篩選' : 'Filter'">
        <button v-for="f in filters" :key="f.id" type="button" class="sc-chip" :class="{ on: filter === f.id }" :style="f.color ? { '--c': f.color } : null" :aria-pressed="filter === f.id" @click="setFilter(f.id)"><i v-if="f.color"></i>{{ f.label }}</button>
        <router-link class="sc-chip sc-chip-link" :to="local('/work/archive/')">{{ zh ? '搜尋全部檔案 →' : 'Search the archive →' }}</router-link>
      </nav>
      <p v-if="activeThread" class="sc-thread-note"><i :style="{ background: activeThread.color }"></i>{{ zh ? activeThread.noteZh : activeThread.noteEn }}</p>

      <!-- 依年份：每一筆都是大圖（A 滿版／B 大圖＋側欄／C 並排） -->
      <template v-for="b in blocks" :key="b.key">
        <template v-if="b.y">
        <header class="sc-year" :id="'y' + b.y.year"><h2>{{ b.y.year }}</h2><span>{{ yearCount(b.y) }}</span></header>
        <template v-for="e in b.y.entries" :key="e.key">
          <article v-if="e.layout === 'A'" class="sc-a gene-host">
            <div class="sc-pic"><img :src="e.item.image" :alt="e.item.alt" loading="lazy" decoding="async" /></div>
            <div class="sc-a-txt">
              <div><span class="sc-kind" :class="{ w: e.item.isWork }">{{ e.item.kind }}</span><h3 class="sc-title"><component :is="linkTag(e.item.primary)" v-bind="linkAttrs(e.item.primary)" class="sc-stretch">{{ e.item.title }}<small>{{ e.item.subtitle }}</small></component></h3><gene-strip v-if="e.item.slug" :slug="e.item.slug" :src="e.item.image" class="sc-gene" /></div>
              <p class="sc-desc">{{ e.item.desc }}</p><p v-if="e.item.credit" class="sc-credit">{{ e.item.credit }}</p>
              <div><dl v-if="e.item.facts.length" class="sc-facts"><template v-for="f in e.item.facts" :key="f.label"><dt>{{ f.label }}</dt><dd>{{ f.value }}</dd></template></dl>
                <p v-if="e.item.threads.length" class="sc-threads"><span v-for="t in e.item.threads" :key="t.id" :style="{ '--c': t.color }">{{ zh ? t.zh : t.en }}</span></p>
                <p class="sc-links"><template v-for="l in e.item.links" :key="l.to || l.href"><a v-if="l.href" :href="l.href" target="_blank" rel="noopener noreferrer">{{ l.label }} ↗︎</a><router-link v-else class="sc-go" :to="local(l.to)">{{ l.label }} →</router-link></template></p></div>
            </div>
          </article>
          <article v-else-if="e.layout === 'B'" class="sc-b gene-host" :class="{ rev: e.rev, tall: e.tall }">
            <div class="sc-pic"><img :src="e.item.image" :alt="e.item.alt" loading="lazy" decoding="async" /></div>
            <div class="sc-b-txt">
              <span class="sc-kind" :class="{ w: e.item.isWork }">{{ e.item.kind }}</span><h3 class="sc-title"><component :is="linkTag(e.item.primary)" v-bind="linkAttrs(e.item.primary)" class="sc-stretch">{{ e.item.title }}<small>{{ e.item.subtitle }}</small></component></h3><gene-strip v-if="e.item.slug" :slug="e.item.slug" :src="e.item.image" class="sc-gene" />
              <p class="sc-desc">{{ e.item.desc }}</p><p v-if="e.item.credit" class="sc-credit">{{ e.item.credit }}</p>
              <component :is="e.item.exhBox.to ? RouterLink : 'div'" v-if="e.item.exhBox" class="sc-exhbox" :class="{ 'is-link': e.item.exhBox.to }" v-bind="e.item.exhBox.to ? { to: local(e.item.exhBox.to) } : {}"><span class="sc-kind">{{ zh ? '展覽' : 'Exhibition' }}</span><b>{{ e.item.exhBox.venue }}</b><p>{{ e.item.exhBox.dates }}<template v-if="e.item.exhBox.photos"> · {{ zh ? `${e.item.exhBox.photos} 張現場紀錄` : `${e.item.exhBox.photos} photographs` }}</template></p><i v-if="e.item.exhBox.to" class="sc-exhbox-go" aria-hidden="true">→</i></component>
              <dl v-else-if="e.item.facts.length" class="sc-facts"><template v-for="f in e.item.facts" :key="f.label"><dt>{{ f.label }}</dt><dd>{{ f.value }}</dd></template></dl>
              <p v-if="e.item.threads.length" class="sc-threads"><span v-for="t in e.item.threads" :key="t.id" :style="{ '--c': t.color }">{{ zh ? t.zh : t.en }}</span></p>
              <p class="sc-links"><template v-for="l in e.item.links" :key="l.to || l.href"><a v-if="l.href" :href="l.href" target="_blank" rel="noopener noreferrer">{{ l.label }} ↗︎</a><router-link v-else class="sc-go" :to="local(l.to)">{{ l.label }} →</router-link></template></p>
            </div>
          </article>
          <div v-else class="sc-c" :class="'n' + e.items.length">
            <article v-for="it in e.items" :key="it.key" class="gene-host">
              <div class="sc-pic"><img :src="it.image" :alt="it.alt" loading="lazy" decoding="async" /></div>
              <span class="sc-kind" :class="{ w: it.isWork }">{{ it.kind }}<template v-if="it.facts.length"> · {{ it.facts[0].value }}</template></span>
              <h3 class="sc-title"><component :is="linkTag(it.primary)" v-bind="linkAttrs(it.primary)" class="sc-stretch">{{ it.title }}<small v-if="zh && it.subtitle">{{ it.subtitle }}</small></component></h3><gene-strip v-if="it.slug" :slug="it.slug" :src="it.image" class="sc-gene" />
              <p class="sc-desc">{{ it.desc }}</p><p v-if="it.credit" class="sc-credit">{{ it.credit }}</p>
              <p v-if="it.threads.length" class="sc-threads"><span v-for="t in it.threads" :key="t.id" :style="{ '--c': t.color }">{{ zh ? t.zh : t.en }}</span></p>
              <p class="sc-links"><template v-for="l in it.links" :key="l.to || l.href"><a v-if="l.href" :href="l.href" target="_blank" rel="noopener noreferrer">{{ l.label }} ↗︎</a><router-link v-else class="sc-go" :to="local(l.to)">{{ l.label }} →</router-link></template></p>
            </article>
          </div>
        </template>
      </template>
        <!-- 商業合作排在 2024 之後、2023 之前（哲宇 2026-10-03：「商業這個類別可以提前一點」） -->
        <template v-else>
          <!-- 星巴克典藏 Dream Plaza：獨立一區（哲宇：「那個也做得很好，可以給他獨立一區塊」） -->
          <section v-if="series" class="sc-series" :aria-label="series.title">
            <div class="sc-series-head">
              <div><p class="sc-eyebrow">{{ series.eyebrow }}</p><h2>{{ series.title }}</h2><gene-strip :slug="series.slug" :src="series.image" class="sc-gene" /></div>
              <p>{{ series.lead }}</p>
            </div>
            <div class="sc-series-grid">
              <figure class="sc-series-main"><router-link class="sc-pic is-link" :to="local(series.to)" tabindex="-1" aria-hidden="true"><img :src="series.image" :alt="series.title" loading="lazy" decoding="async" /></router-link><figcaption v-if="series.credit">{{ series.credit }}</figcaption></figure>
              <figure v-for="g in series.gallery" :key="g.image"><router-link class="sc-pic is-link" :to="local(series.to)" tabindex="-1" aria-hidden="true"><img :src="g.image" :alt="g.caption" loading="lazy" decoding="async" /></router-link><figcaption>{{ g.caption }}</figcaption></figure>
            </div>
            <p class="sc-links"><router-link class="sc-go" :to="local(series.to)">{{ zh ? '進入作品' : 'View work' }} →</router-link></p>
          </section>

          <!-- 商業合作與委託 -->
          <section class="sc-comm" :aria-labelledby="'sc-comm-h'">
            <p class="sc-eyebrow">Commissions &amp; Collaborations</p>
            <h2 id="sc-comm-h">{{ zh ? '商業合作與委託' : 'Commissions and collaborations' }}</h2>
            <p class="sc-sub">{{ zh ? '品牌、機構與表演的委託作品。' : 'Work made for brands, institutions and performances.' }}</p>
            <div class="sc-comm-grid">
              <article v-for="c in commissions" :key="c.key" class="gene-host">
                <div class="sc-pic"><img :src="c.image" :alt="c.alt" loading="lazy" decoding="async" /></div>
                <span class="sc-kind">{{ c.client }}<template v-if="c.year"> · {{ c.year }}</template></span>
                <h3 class="sc-title"><component :is="linkTag(c.primary)" v-bind="linkAttrs(c.primary)" class="sc-stretch">{{ c.title }}</component></h3><gene-strip v-if="c.slug" :slug="c.slug" :src="c.image" class="sc-gene" />
                <p class="sc-desc">{{ c.desc }}</p>
                <p class="sc-links"><template v-for="l in c.links" :key="l.to || l.href"><a v-if="l.href" :href="l.href" target="_blank" rel="noopener noreferrer">{{ l.label }} ↗︎</a><router-link v-else class="sc-go" :to="local(l.to)">{{ l.label }} →</router-link></template></p>
              </article>
            </div>
            <h3 v-if="commissionList.length" class="sc-comm-list-h">{{ zh ? showcase.commissions.listTitleZh : showcase.commissions.listTitleEn }}</h3>
            <div v-if="commissionList.length" class="sc-comm-list">
              <div v-for="c in commissionList" :key="c.title"><span>{{ c.year }}</span><b>{{ c.title }}</b><i>{{ c.note }}</i></div>
            </div>
          </section>
        </template>
      </template>
      <p v-if="!visibleYears.length" class="sc-empty">{{ zh ? '這條線上目前沒有作品。' : 'Nothing on this thread yet.' }}</p>

      <!-- 完整清單：一頁看完全部 -->
      <section class="sc-index" aria-labelledby="sc-index-h">
        <p class="sc-eyebrow">Index</p>
        <h2 id="sc-index-h">{{ zh ? '完整清單' : 'Index' }}</h2>
        <table>
          <thead><tr><th>{{ zh ? '年份' : 'Year' }}</th><th>{{ zh ? '名稱' : 'Title' }}</th><th>{{ zh ? '類型' : 'Type' }}</th><th>{{ zh ? '場地或平台' : 'Venue / platform' }}</th></tr></thead>
          <tbody><tr v-for="r in indexRows" :key="r.key"><td class="y">{{ r.year }}</td><td class="t"><router-link :to="local(r.to)">{{ r.title }}</router-link></td><td>{{ r.kind }}</td><td>{{ r.venue }}</td></tr></tbody>
        </table>
        <router-link class="sc-toarchive" :to="local('/work/archive/')">
          <span><span class="sc-eyebrow">Archive</span><b>{{ zh ? '完整檔案' : 'The full archive' }}</b><i>{{ zh ? `搜尋與篩選全部 ${counts.archive} 筆：作品、展覽、早期專案與每日創作` : `Search and filter all ${counts.archive} records: works, exhibitions, early projects and daily creations` }}</i></span>
          <span class="go">{{ zh ? '進入 →' : 'Open →' }}</span>
        </router-link>
      </section>

      <nav class="sc-more" :aria-label="zh ? '延伸' : 'More'">
        <router-link :to="local('/nft/')">{{ zh ? 'NFT 收藏' : 'NFT collections' }}<span>{{ zh ? '版次資料庫' : 'Edition databases' }}</span></router-link>
        <router-link :to="local('/lineage/')">{{ zh ? '創作族譜' : 'Family tree' }}<span>{{ zh ? '每日創作的血緣' : 'How the daily works descend' }}</span></router-link>
        <router-link :to="local('/lab/')">{{ zh ? '實驗室' : 'Lab' }}<span>lab.cheyuwu.com</span></router-link>
        <router-link :to="local('/cv/')">{{ zh ? '完整履歷' : 'CV' }}<span>{{ zh ? '展覽、獎項與演講' : 'Exhibitions, awards, talks' }}</span></router-link>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { withSlash } from '@/runtime/site-path.mjs'
import { resolveMedia } from '@/components/work/record-utils'
import showcase, { resolveItem, entryItems, workBySlug, exhibitionBySlug, exhibitionPath, threads, threadById } from '@/components/showcase/showcase-resolve.mjs'
import catalog from '@/data/work-catalog.json'
import exhibitions from '@/data/exhibitions.json'
import legacyProjects from '@/data/legacy-projects.json'
import tdCatalog from '@/data/td-catalog.json'
import sketchSelection from '@/data/sketch-selection.json'
import GeneStrip from '@/components/GeneStrip.vue'

const route = useRoute(), router = useRouter(), { locale } = useI18n()
const zh = computed(() => locale.value === 'zh')
const local = p => (/^https?:/.test(p) ? p : withSlash((zh.value ? '/zh' : '') + p))

// 舊連結 /work/?category=…&year=…&q=… 是篩選結果，現在住在完整檔案子頁：帶著條件換過去
onMounted(() => {
  const q = route.query
  if (['q', 'category', 'year', 'type'].some(k => typeof q[k] === 'string' && q[k])) router.replace({ path: local('/work/archive/'), query: q })
})

// 整個作品區塊都可以點（哲宇 2026-10-03：「點擊那個整個作品區塊」「希望大家能夠很直覺點進去」）：
// 標題就是主連結，用 ::after 撐滿整塊（stretched link），點圖、點說明都進同一個地方；站內走 router-link（作品頁會接「作品浮出」形變），
// 站外開新分頁。同一筆有作品也有展覽時，作品是主連結，展覽盒自己是一張卡、浮在上面，點了去展覽頁。
const linkTag = l => (l?.to ? RouterLink : l?.href ? 'a' : 'span')
const linkAttrs = l => (l?.to ? { to: local(l.to) } : l?.href ? { href: l.href, target: '_blank', rel: 'noopener noreferrer' } : {})

// 頁首的年份範圍跟著年份段落走
const yearSpan = `${Math.min(...showcase.years.map(y => y.year))}–${Math.max(...showcase.years.map(y => y.year))}`

// 數字：作品、展覽、完整檔案（跟子頁同一套來源）
const counts = computed(() => ({
  works: catalog.length - (showcase.omitWorks || []).length,
  exhibitions: exhibitions.length + Object.values(showcase.extra || {}).filter(x => x.kind === 'exhibition').length,
  archive: catalog.length + exhibitions.length + Object.values(legacyProjects).filter(w => w.show && w.cover && w.cover !== '/img/default.jpg').length + tdCatalog.works.filter(w => w.public && (w.cover || w.poster)).length + sketchSelection.ids.length,
}))

// 頁首輪播：7 秒換一張；使用者偏好減少動態就不自動換
const heroSlides = computed(() => showcase.hero.map(h => {
  const w = workBySlug[h.work]
  const item = resolveItem({ work: h.work }, zh.value)
  return { key: h.work, image: resolveMedia(h.image), width: h.width, height: h.height, alt: zh.value ? h.altZh : h.altEn, title: item.title, meta: [w?.year, item.kind].filter(Boolean).join(' · '), to: local(`/work/${h.work}/`) }
}))
const slide = ref(0)
let timer = null
const go = i => { slide.value = i; restart() }
function restart() {
  clearInterval(timer)
  if (typeof window === 'undefined' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
  timer = setInterval(() => { slide.value = (slide.value + 1) % heroSlides.value.length }, 7000)
}
onMounted(restart)
onBeforeUnmount(() => clearInterval(timer))

const essay = computed(() => (zh.value ? showcase.essay?.zh : showcase.essay?.en) || { lede: '', body: [] })

// 篩選：全部／作品／展覽／五條線。URL 帶 ?view= 讓分享出去的連結停在同一個篩選
const filters = computed(() => [
  { id: 'all', label: zh.value ? '全部' : 'All' },
  { id: 'work', label: zh.value ? '作品' : 'Works' },
  { id: 'exhibition', label: zh.value ? '展覽' : 'Exhibitions' },
  ...threads.map(t => ({ id: t.id, label: zh.value ? t.zh : t.en, color: t.color })),
])
const filter = computed(() => (typeof route.query.view === 'string' && filters.value.some(f => f.id === route.query.view) ? route.query.view : 'all'))
const setFilter = id => { const q = { ...route.query }; if (id === 'all') delete q.view; else q.view = id; router.replace({ query: q }) }
const activeThread = computed(() => threadById[filter.value] || null)
const matches = (entry, item) => {
  const f = filter.value
  if (f === 'all') return true
  if (f === 'work') return item.isWork
  if (f === 'exhibition') return item.isExhibition
  return (entry.threads || []).includes(f)
}

const years = computed(() => showcase.years.map(y => ({
  year: y.year,
  entries: y.entries.map((e, i) => {
    if (e.layout === 'C') {
      const items = e.items.map(it => ({ src: it, item: resolveItem(it, zh.value) })).filter(({ src, item }) => matches(src, item)).map(({ item }) => item)
      return items.length ? { key: y.year + '-' + i, layout: 'C', items } : null
    }
    const item = resolveItem(e, zh.value)
    return matches(e, item) ? { key: y.year + '-' + i, layout: e.layout, rev: e.rev, tall: e.tall, item } : null
  }).filter(Boolean),
})))
const visibleYears = computed(() => years.value.filter(y => y.entries.length))
// 年份段落與商業區排成一串：星巴克專區＋商業合作插在 2023 年之前（篩選時照樣顯示）
const COMMERCE_BEFORE = 2023
const blocks = computed(() => {
  const out = []
  let placed = false
  for (const y of visibleYears.value) {
    if (!placed && y.year <= COMMERCE_BEFORE) { out.push({ key: 'commerce', commerce: true }); placed = true }
    out.push({ key: y.year, y })
  }
  if (!placed) out.push({ key: 'commerce', commerce: true })
  return out
})
const yearCount = y => {
  let w = 0, x = 0
  for (const e of y.entries) for (const it of e.layout === 'C' ? e.items : [e.item]) { if (it.isWork) w++; if (it.isExhibition) x++ }
  return zh.value ? [w && `${w} 件作品`, x && `${x} 場展覽`].filter(Boolean).join(' · ') : [w && `${w} work${w > 1 ? 's' : ''}`, x && `${x} exhibition${x > 1 ? 's' : ''}`].filter(Boolean).join(' · ')
}

const series = computed(() => {
  const s = showcase.series
  if (!s) return null
  const w = workBySlug[s.work]
  return {
    eyebrow: s.eyebrowEn, title: zh.value ? s.titleZh : s.titleEn, lead: zh.value ? s.leadZh : s.leadEn, to: `/work/${w.slug}/`, slug: w.slug,
    image: resolveMedia(s.image), credit: zh.value ? s.creditZh : s.creditEn, gallery: s.gallery.map(g => ({ image: resolveMedia(g.image), caption: zh.value ? g.zh : g.en })),
  }
})

const commissions = computed(() => (showcase.commissions?.featured || []).map(c => {
  const it = resolveItem(c, zh.value)
  return { ...it, client: zh.value ? c.clientZh : c.clientEn }
}))
const commissionList = computed(() => (showcase.commissions?.list || []).map(c => ({ year: c.year, title: zh.value ? c.titleZh : c.titleEn, note: zh.value ? c.noteZh : c.noteEn })))

// 完整清單：主頁上每一筆，依年份新到舊
const indexRows = computed(() => {
  const rows = []
  const add = (src, item) => {
    const x = src.exhibition ? exhibitionBySlug[src.exhibition] : null
    const ex = src.extra ? showcase.extra?.[src.extra] : null
    const venue = x ? (zh.value ? x.venue?.zh || x.locationZh : x.venue?.en || x.location)
      : ex?.venueZh ? [zh.value ? ex.venueZh : ex.venueEn, zh.value ? ex.cityZh : ex.cityEn].filter(Boolean).join(zh.value ? '，' : ', ')
      : (src.clientZh ? (zh.value ? src.clientZh : src.clientEn) : '')
    rows.push({ key: item.key + rows.length, year: item.year, title: item.title, kind: item.kind, venue: venue || '', to: item.links.find(l => l.to)?.to || '/work/' })
  }
  for (const y of showcase.years) for (const e of y.entries) for (const it of entryItems(e)) add(it, resolveItem(it, zh.value))
  if (showcase.series) add({ work: showcase.series.work }, resolveItem({ work: showcase.series.work }, zh.value))
  for (const c of showcase.commissions?.featured || []) add(c, resolveItem(c, zh.value))
  return rows.sort((a, b) => (b.year || 0) - (a.year || 0))
})
</script>

<style scoped>
.showcase { background: var(--bg0); color: var(--ink); text-align: left; }
.sc-wrap { max-width: 1360px; margin: 0 auto; padding: 0 40px; }
.sc-eyebrow { font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--ink-2); margin: 0; }
.sc-label { font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: var(--ink-3); margin: 0 0 18px; grid-column: 1 / -1; }
/* 頁首輪播 */
.sc-hero { position: relative; height: calc(100svh - 64px); min-height: 620px; overflow: hidden; background: #000; }
.sc-hero-slide { position: absolute; inset: 0; opacity: 0; transition: opacity 1.2s var(--ease-out, ease); }
.sc-hero-slide.on { opacity: 1; }
.sc-hero-slide:not(.on) { pointer-events: none; }
.sc-hero-slide img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sc-hero::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(180deg, rgba(0,0,0,.35) 0%, rgba(0,0,0,0) 26%, rgba(0,0,0,0) 48%, rgba(0,0,0,.92) 100%); }
.sc-hero-cap { position: absolute; z-index: 2; left: 40px; right: 40px; bottom: 64px; display: flex; justify-content: space-between; align-items: flex-end; gap: 40px; }
.sc-hero h1 { font-family: var(--font-display); font-weight: 600; font-size: clamp(56px, 8vw, 120px); line-height: 1; margin: 14px 0 0; letter-spacing: 0; }
.sc-count { margin: 20px 0 0; font-size: 14px; color: var(--ink-2); display: flex; flex-wrap: wrap; gap: 4px 10px; align-items: baseline; }
.sc-count b { color: var(--ink); font-weight: 500; }
.sc-count span { color: var(--ink-3); }
.sc-count a { color: var(--gold); text-decoration: none; }
.sc-hero-now { text-align: right; font-size: 13px; color: var(--ink-2); text-decoration: none; max-width: 22em; }
.sc-hero-now b { display: block; font-family: var(--font-display); font-weight: 600; font-size: 20px; color: var(--ink); margin: 4px 0; }
.sc-hero-now i { font-style: normal; color: var(--gold); }
.sc-dots { position: absolute; z-index: 2; right: 40px; bottom: 30px; display: flex; gap: 8px; }
.sc-dots button { width: 26px; height: 22px; border: 0; padding: 0; background: none; cursor: pointer; position: relative; }
.sc-dots button::after { content: ''; position: absolute; left: 0; right: 0; top: 10px; height: 2px; background: rgba(255,255,255,.3); transition: var(--t-hover); }
.sc-dots button.on::after { background: var(--gold); }
/* 策展論述 */
.sc-essay { display: grid; grid-template-columns: 1fr 1fr; gap: 0 72px; padding: 120px 0 64px; }
.sc-lede { font-family: var(--font-display); font-weight: 500; font-size: 28px; line-height: 1.72; margin: 0; position: sticky; top: 140px; align-self: start; }
.sc-essay-body p { font-family: var(--font-display); font-size: 18px; line-height: 2; color: var(--ink-2); margin: 0 0 22px; }
.sc-essay-full summary { cursor: pointer; color: var(--gold); font-size: 14px; min-height: 44px; display: flex; align-items: center; }
.sc-essay-full summary:focus-visible { outline: 2px solid var(--gold); outline-offset: 4px; }
.sc-essay-full[open] summary { margin-bottom: 18px; }
.sc-essay-full h3 { font-family: var(--font-display); font-size: 22px; margin: 40px 0 14px; color: var(--ink); }
.sc-sig { grid-column: 1 / -1; margin: 44px 0 0; padding-top: 26px; border-top: 1px solid var(--line); display: flex; gap: 28px; align-items: baseline; flex-wrap: wrap; }
.sc-sig p { font-family: var(--font-display); font-size: 21px; margin: 0; color: var(--ink); }
.sc-sig cite { font-style: normal; font-size: 13px; color: var(--ink-3); }
/* 篩選列 */
/* 背景與上下線鋪滿整個視窗寬（2026-10-03 哲宇：「上面的 bar 看起來寬度不夠」）：box-shadow 與 border-image 的外擴都只是繪製溢出，不產生橫向捲軸 */
.sc-bar { position: sticky; top: 64px; z-index: 30; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; padding: 14px 0; background: rgba(0,0,0,.94); box-shadow: 0 0 0 100vmax rgba(0,0,0,.94); clip-path: inset(0 -100vmax); border-block: 1px solid; border-image: linear-gradient(var(--line), var(--line)) 1 / 1px 0 / 0 100vmax; }
.sc-chip { display: inline-flex; align-items: center; gap: 8px; min-height: 36px; padding: 6px 15px; border: 0; border-radius: var(--r-pill); background: var(--tint-2); color: var(--ink-2); font: inherit; font-size: 13px; cursor: pointer; text-decoration: none; transition: var(--t-hover); white-space: nowrap; }
.sc-chip:hover { background: var(--tint-3); color: var(--ink); }
.sc-chip.on { background: var(--ink); color: #000; }
.sc-chip i { width: 7px; height: 7px; border-radius: 50%; background: var(--c); }
.sc-chip:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.sc-chip-link { margin-left: auto; background: none; color: var(--gold); }
.sc-thread-note { display: flex; gap: 12px; align-items: baseline; margin: 28px 0 0; font-family: var(--font-display); font-size: 18px; color: var(--ink-2); max-width: 46em; }
.sc-thread-note i { flex: none; width: 8px; height: 8px; border-radius: 50%; transform: translateY(-2px); }
/* 年份 */
.sc-year { display: flex; align-items: baseline; gap: 24px; padding: 110px 0 36px; scroll-margin-top: 40px; }
.sc-year h2 { font-family: var(--font-display); font-weight: 600; font-size: 128px; line-height: .9; margin: 0; color: var(--ink); }
.sc-year span { color: var(--ink-3); font-size: 14px; }
/* 共用 */
.sc-pic { overflow: hidden; background: var(--bg1); }
.sc-pic img { display: block; width: 100%; height: 100%; object-fit: cover; }
/* 整塊可以點：標題連結的 ::after 撐滿整個 article；文字連結與展覽盒浮在上面各自可點。
   滑過整塊時圖放大 1.025、第一個文字連結變亮（跟首頁精選同一套；按下框縮 .985 在 motion.css） */
.sc-a, .sc-b, .sc-c article, .sc-comm-grid article { position: relative; }
.sc-stretch { color: inherit; text-decoration: none; }
.sc-stretch:hover, .sc-stretch:focus { color: inherit; text-decoration: none; }
a.sc-stretch::after { content: ''; position: absolute; inset: 0; z-index: 1; }
a.sc-stretch:focus-visible { outline: none; }
a.sc-stretch:focus-visible::after { outline: 2px solid var(--gold); outline-offset: 6px; }
.sc-links, .sc-exhbox { position: relative; z-index: 2; }
@media (hover: hover) {
  :is(.sc-a, .sc-b, .sc-c article, .sc-comm-grid article):has(a.sc-stretch):hover .sc-pic img { transform: scale(1.025); }
  :is(.sc-a, .sc-b, .sc-c article, .sc-comm-grid article):has(a.sc-stretch):hover .sc-links a:first-child { color: var(--ink); }
}
.sc-pic.is-link { display: block; cursor: pointer; }
@media (hover: hover) { .sc-pic.is-link:hover img { transform: scale(1.025); } }
.sc-kind { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-2); }
.sc-kind::before { content: ''; flex: none; width: 7px; height: 7px; border-radius: 50%; background: var(--gold); }
.sc-kind.w::before { background: var(--ink); }
.sc-title { font-family: var(--font-display); font-weight: 600; font-size: 44px; line-height: 1.2; margin: 12px 0 0; }
/* 基因條（2026-10-04）：作品名下方一條，從封面橫切一刀的條碼；只有作品帶，展覽不帶。滑過整張卡時條碼流動（GeneStrip.vue） */
.sc-gene { --gene-h: 16px; margin-top: 18px; }
.sc-c.n3 .sc-gene, .sc-comm-grid .sc-gene { --gene-h: 14px; margin-top: 14px; }
.sc-title small { display: block; font-family: var(--font-body); font-weight: 500; font-size: 15px; color: var(--ink-3); margin-top: 8px; letter-spacing: .02em; }
.sc-desc { font-family: var(--font-display); font-weight: 500; font-size: 18px; line-height: 1.95; color: var(--ink); margin: 18px 0 0; }
.sc-credit { margin: 10px 0 0; font-size: 12px; color: var(--ink-3); letter-spacing: .02em; }
.sc-facts { display: grid; grid-template-columns: auto 1fr; gap: 6px 18px; margin: 22px 0 0; font-size: 14px; }
.sc-facts dt { color: var(--ink-3); margin: 0; font-weight: 400; }
.sc-facts dd { color: var(--ink-2); margin: 0; }
.sc-threads { display: flex; flex-wrap: wrap; gap: 14px; margin: 20px 0 0; font-size: 12px; color: var(--ink-2); }
.sc-threads span { display: inline-flex; align-items: center; gap: 7px; }
.sc-threads span::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--c); }
.sc-links { display: flex; flex-wrap: wrap; gap: 8px 26px; margin: 24px 0 0; font-size: 15px; }
.sc-links a { color: var(--gold); text-decoration: none; }
.sc-links a:hover { color: var(--ink); }
.sc-exhbox { display: block; margin-top: 26px; padding: 20px 22px; background: var(--bg1); color: inherit; text-decoration: none; transition: background var(--dur-quick, .2s) var(--ease-out, ease); }
.sc-exhbox.is-link:hover { background: var(--tint-2); color: inherit; text-decoration: none; }
.sc-exhbox-go { position: absolute; top: 18px; right: 20px; font-style: normal; color: var(--gold); transition: transform var(--dur-quick, .2s) var(--ease-out, ease); }
.sc-exhbox.is-link:hover .sc-exhbox-go { transform: translateX(3px); }
.sc-exhbox .sc-kind { font-size: 11px; }
.sc-exhbox b { display: block; font-family: var(--font-display); font-size: 19px; margin: 6px 0 2px; }
.sc-exhbox p { margin: 0; font-size: 13px; color: var(--ink-2); }
/* A：滿版 */
.sc-a { margin: 0 -40px; }
.sc-a .sc-pic { height: min(72vh, 760px); }
.sc-a-txt { display: grid; grid-template-columns: 1.1fr 1.3fr 1fr; gap: 56px; padding: 40px 40px 0; }
.sc-a-txt .sc-desc { margin-top: 0; }
/* B：大圖＋側欄 */
.sc-b { display: grid; grid-template-columns: 1.55fr 1fr; gap: 56px; align-items: start; margin-top: 100px; }
.sc-b.rev { grid-template-columns: 1fr 1.55fr; }
.sc-b.rev .sc-pic { order: 2; }
.sc-b .sc-pic { aspect-ratio: 4 / 3; }
.sc-b.tall .sc-pic { aspect-ratio: 4 / 5; }
/* C：並排 */
.sc-c { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 100px; }
.sc-c.n1 { grid-template-columns: minmax(0, 720px); }
.sc-c.n3 { grid-template-columns: repeat(3, 1fr); }
.sc-c .sc-pic { aspect-ratio: 4 / 3; margin-bottom: 24px; }
.sc-c .sc-title { font-size: 32px; }
.sc-c.n3 .sc-title { font-size: 26px; }
.sc-c.n3 .sc-desc { font-size: 16px; }
.sc-empty { padding: 80px 0; color: var(--ink-3); }
/* 星巴克專區 */
.sc-series { margin: 140px -40px 0; padding: 90px 40px; background: #0b0907; }
.sc-series-head { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: end; margin-bottom: 44px; }
.sc-series h2 { font-family: var(--font-display); font-weight: 600; font-size: 56px; line-height: 1.15; margin: 10px 0 0; }
.sc-series-head p { font-family: var(--font-display); font-size: 18px; line-height: 1.95; color: var(--ink-2); margin: 0; }
.sc-series-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.sc-series-main { grid-column: 1 / -1; display: block; }
.sc-series .sc-series-main .sc-pic { aspect-ratio: 16 / 9; }
.sc-series figure { margin: 0; }
.sc-series figure .sc-pic { aspect-ratio: 3 / 2; }
.sc-series figcaption { font-size: 13px; color: var(--ink-3); margin-top: 10px; }
/* 商業合作 */
.sc-comm { margin-top: 140px; }
.sc-comm h2, .sc-index h2 { font-family: var(--font-display); font-weight: 600; font-size: 48px; margin: 10px 0 8px; }
.sc-sub { color: var(--ink-3); font-size: 14px; margin: 0; }
.sc-comm-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px 40px; margin-top: 44px; }
.sc-comm-grid .sc-pic { aspect-ratio: 3 / 2; margin-bottom: 20px; }
.sc-comm-grid .sc-title { font-size: 30px; }
/* 委託大圖是單數時，第一件跨兩欄（不留孤兒格） */
.sc-comm-grid article:first-child:nth-last-child(odd) { grid-column: 1 / -1; }
.sc-comm-grid article:first-child:nth-last-child(odd) .sc-pic { aspect-ratio: 21 / 9; }
.sc-comm-list-h { font-family: var(--font-display); font-weight: 600; font-size: 22px; margin: 90px 0 0; }
.sc-comm-list { margin-top: 20px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 0 40px; }
.sc-comm-list div { padding: 16px 0; border-top: 1px solid var(--line); display: grid; grid-template-columns: 52px 1fr; gap: 4px 12px; }
.sc-comm-list span { color: var(--ink-3); font-size: 13px; }
.sc-comm-list b { font-family: var(--font-display); font-size: 16px; }
.sc-comm-list i { grid-column: 2; font-style: normal; color: var(--ink-3); font-size: 12px; }
/* 清單 */
.sc-index { margin-top: 140px; }
.sc-index table { width: 100%; border-collapse: collapse; font-size: 15px; margin-top: 24px; }
.sc-index th { font-size: 12px; letter-spacing: .14em; color: var(--ink-3); text-align: left; font-weight: 500; padding: 10px 16px 10px 0; border-bottom: 1px solid var(--line-2, rgba(255,255,255,.15)); }
.sc-index td { padding: 15px 16px 15px 0; border-bottom: 1px solid var(--line); color: var(--ink-2); vertical-align: top; }
.sc-index td.y { color: var(--ink-3); width: 80px; }
.sc-index td.t a { font-family: var(--font-display); font-weight: 600; font-size: 18px; color: var(--ink); text-decoration: none; }
.sc-index td.t a:hover { color: var(--gold); }
.sc-toarchive { display: grid; grid-template-columns: 1fr auto; gap: 40px; align-items: center; margin-top: 56px; padding: 40px 48px; background: var(--bg1); text-decoration: none; color: var(--ink); transition: var(--t-hover); }
.sc-toarchive:hover { background: var(--bg2); }
.sc-toarchive b { display: block; font-family: var(--font-display); font-weight: 600; font-size: 38px; margin: 8px 0 6px; }
.sc-toarchive i { font-style: normal; color: var(--ink-2); font-size: 15px; }
.sc-toarchive .go { font-size: 18px; color: var(--gold); }
.sc-more { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; padding: 100px 0 140px; }
.sc-more a { border-top: 1px solid var(--line); padding-top: 16px; font-family: var(--font-display); font-weight: 600; font-size: 19px; color: var(--ink); text-decoration: none; }
.sc-more a span { display: block; font-family: var(--font-body); font-weight: 400; font-size: 13px; color: var(--ink-3); margin-top: 4px; }
.sc-more a:hover { color: var(--gold); }
@media (max-width: 1100px) {
  .sc-a-txt { grid-template-columns: 1fr 1fr; }
  .sc-a-txt > div:last-child { grid-column: 1 / -1; }
  .sc-c.n3 { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 760px) {
  .sc-wrap { padding: 0 18px; }
  .sc-hero { height: calc(88svh - 56px); min-height: 520px; }
  .sc-hero-cap { left: 18px; right: 18px; bottom: 46px; flex-direction: column; align-items: flex-start; gap: 18px; }
  .sc-hero h1 { font-size: 46px; }
  .sc-count { font-size: 13px; }
  .sc-hero-now { text-align: left; }
  .sc-hero-now b { font-size: 17px; }
  .sc-dots { left: 18px; right: auto; bottom: 16px; }
  .sc-essay { grid-template-columns: 1fr; padding: 72px 0 48px; }
  .sc-lede { font-size: 21px; line-height: 1.75; margin-bottom: 22px; position: static; }
  .sc-essay-body p { font-size: 16px; }
  .sc-sig { flex-direction: column; gap: 6px; margin-top: 28px; }
  .sc-sig p { font-size: 18px; }
  .sc-bar { top: 56px; flex-wrap: nowrap; overflow-x: auto; margin: 0 -18px; padding: 12px 18px; scrollbar-width: none; }
  .sc-bar::-webkit-scrollbar { display: none; }
  .sc-chip { flex: none; }
  .sc-chip-link { margin-left: 0; }
  .sc-year { padding: 80px 0 24px; gap: 14px; }
  .sc-year h2 { font-size: 76px; }
  .sc-year span { font-size: 12px; }
  .sc-a { margin: 0 -18px; }
  .sc-a .sc-pic { height: auto; aspect-ratio: 4 / 5; }
  .sc-a-txt { grid-template-columns: 1fr; gap: 0; padding: 24px 18px 0; }
  .sc-a-txt .sc-desc { margin-top: 18px; }
  .sc-b, .sc-b.rev { grid-template-columns: 1fr; gap: 22px; margin-top: 72px; }
  .sc-b.rev .sc-pic { order: 0; }
  .sc-b .sc-pic, .sc-b.tall .sc-pic { aspect-ratio: 4 / 5; margin: 0 -18px; }
  .sc-c, .sc-c.n3 { grid-template-columns: 1fr; gap: 64px; margin-top: 72px; }
  .sc-c .sc-pic { margin: 0 -18px 20px; }
  .sc-title { font-size: 31px; }
  .sc-c .sc-title, .sc-c.n3 .sc-title { font-size: 27px; }
  .sc-desc { font-size: 16px; line-height: 1.9; }
  .sc-series { margin: 96px -18px 0; padding: 64px 18px; }
  .sc-series-head { grid-template-columns: 1fr; gap: 16px; }
  .sc-series h2 { font-size: 38px; }
  .sc-series-grid { grid-template-columns: 1fr; }
  .sc-series .sc-series-main .sc-pic { aspect-ratio: 3 / 2; }
  .sc-comm h2, .sc-index h2 { font-size: 34px; }
  .sc-comm-grid { grid-template-columns: 1fr; gap: 56px; }
  .sc-comm-grid .sc-pic { margin: 0 -18px 18px; }
  .sc-comm-grid article:first-child:nth-last-child(odd) .sc-pic { aspect-ratio: 3 / 2; }
  .sc-comm-list { grid-template-columns: 1fr; }
  .sc-index thead { display: none; }
  .sc-index table, .sc-index tbody, .sc-index tr, .sc-index td { display: block; }
  .sc-index tr { display: grid; grid-template-columns: 52px 1fr; gap: 2px 10px; padding: 14px 0; border-bottom: 1px solid var(--line); }
  .sc-index td { border: 0; padding: 0; }
  .sc-index td.y { grid-row: 1 / span 3; width: auto; padding-top: 3px; }
  .sc-index td:nth-child(n+2) { grid-column: 2; }
  .sc-index td:nth-child(n+3) { font-size: 12px; color: var(--ink-3); }
  .sc-index td.t a { font-size: 17px; }
  .sc-toarchive { grid-template-columns: 1fr; padding: 30px 22px; margin: 44px -18px 0; }
  .sc-toarchive b { font-size: 30px; }
  .sc-more { grid-template-columns: 1fr 1fr; }
}
@media (prefers-reduced-motion: reduce) { .sc-hero-slide { transition: none; } }
</style>
